import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useResizeDetector } from 'react-resize-detector';
import styled from 'styled-components';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';

import SettlementFilterModal from './components/SettlementFilterModal';
import SettlementTable from './components/SettlementTable';
import {
  FilterType,
  useSettlementDetailHooks,
} from './hooks/useSettlementDetailHooks';
import Text from 'components/common/Text';
import { SETTLEMENT_REQUEST_PATH } from 'constants/path';
import SettlementRequestModal from 'containers/SettlementRequest/components/SettlementRequestModal';
import { ICON_ARROW_BLACK_BOTTOM, ICON_NAVIGATE_BACK } from 'assets/icons';
import HackleManager from 'utils/hackle';
import Row from 'components/common/Row';
import DetailHeader from './components/DetailHeader';
import SettlementDetailList from './components/SettlementDetailList';
import EmptySettlement from 'containers/Settlement/components/EmptySettlement';
import { MAX_WIDTH } from 'App';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  width: 100%;
`;

const Container = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  padding: ${props => (props.$isMobile ? '0 20px' : '0 40px 40px')};
  margin: 0 auto;

  width: 100%;
  max-width: ${props => (props.$isMobile ? '100%' : '1200px')};
  min-height: 100vh;

  border-left: 1px solid #dbdcdf;
  border-right: 1px solid #dbdcdf;

  background-color: #fff;

  @media (max-width: 768px) {
    border-left: none;
    border-right: none;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 0 24px;
`;

const SettlementBox = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 0 20px;
  width: 100%;
`;

const FilterButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 6px 8px;

  border-radius: 9999px;
  border: 1px solid #eaebec;
  background: #fff;

  color: #1b1c1e;

  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.6px;
`;

const SettlementDetailPage = () => {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  dayjs.locale('ko');

  const { ref, width } = useResizeDetector();

  const isMobile = width ? width < MAX_WIDTH : window.innerWidth < MAX_WIDTH;

  const {
    settlementItem,
    settlementAccountList,
    sortedAccountList,
    filterType,
    handleAccountListFilter,
    handlePageChange,
  } = useSettlementDetailHooks();

  const [
    isOpenSettlementInformationModal,
    setIsOpenSettlementInformationModal,
  ] = useState<boolean>(false);
  const [isOpenFilterModal, setIsOpenFilterModal] = useState<boolean>(false);

  const handlePageBackwardNavigate = () => {
    if (isOpenSettlementInformationModal) {
      setIsOpenSettlementInformationModal(false);

      return;
    }

    if (isOpenFilterModal) {
      setIsOpenFilterModal(false);

      return;
    }

    navigate(SETTLEMENT_REQUEST_PATH, { state: { id: params.id } });
  };

  useEffect(() => {
    window.handlePageBackwardNavigate = handlePageBackwardNavigate;
  }, [isOpenFilterModal, isOpenSettlementInformationModal]);

  useEffect(() => {
    HackleManager.logSettlementDetailView(params.id);
  }, []);

  return (
    <Wrapper ref={ref}>
      {settlementItem && (
        <Container $isMobile={isMobile}>
          <DetailHeader
            isMobile={isMobile}
            settlement={settlementItem.settlement.settlement}
          />
          {settlementAccountList && sortedAccountList && (
            <ContentContainer>
              <Row
                justifyContent="space-between"
                style={{
                  position: 'sticky',
                  top: isMobile ? '124px' : '157px',
                  paddingBottom: '24px',
                  width: '100%',
                  background: '#fff',
                }}
              >
                <Text color="#1b1c1e" size="20px">
                  총 건수:{' '}
                  {settlementAccountList.settlement.accounts.totalCount}
                </Text>
                <FilterButton onClick={() => setIsOpenFilterModal(true)}>
                  <span>
                    {filterType === FilterType.DATE
                      ? '결제일순'
                      : filterType === FilterType.TYPE
                      ? '유형별'
                      : '상품별'}
                  </span>
                  <img src={ICON_ARROW_BLACK_BOTTOM} alt="drop down icon" />
                </FilterButton>
              </Row>
              {settlementAccountList.settlement.accounts.totalCount === 0 ? (
                <EmptySettlement textContent="조회되는 내역이 없어요" />
              ) : isMobile ? (
                <SettlementDetailList
                  accountItem={sortedAccountList}
                  hasNextPage={
                    settlementAccountList.settlement.accounts.pageInfo
                      .hasNextPage
                  }
                  handlePageChange={handlePageChange}
                />
              ) : (
                <SettlementTable
                  accountItem={sortedAccountList}
                  hasNextPage={
                    settlementAccountList.settlement.accounts.pageInfo
                      .hasNextPage
                  }
                  handlePageChange={handlePageChange}
                />
              )}
            </ContentContainer>
          )}
          {isOpenFilterModal && (
            <SettlementFilterModal
              isMobile={isMobile}
              handleAccountListFilter={handleAccountListFilter}
              onClose={() => setIsOpenFilterModal(false)}
            />
          )}
          {isOpenSettlementInformationModal && (
            <SettlementRequestModal
              isMobile={true}
              type="information"
              settlementItem={settlementItem!.settlement.settlement}
              onClose={() => setIsOpenSettlementInformationModal(false)}
            />
          )}
        </Container>
      )}
    </Wrapper>
  );
};

export default SettlementDetailPage;
